import { useState } from "react";
import { api } from "../api/staff";

type Tier = "advanced" | "pro" | "pro_plus" | "max";

const TIER_OPTIONS: { value: Tier; label: string }[] = [
  { value: "advanced", label: "Advanced" },
  { value: "pro", label: "Pro" },
  { value: "pro_plus", label: "Pro+" },
  { value: "max", label: "Max" },
];

const DURATION_OPTIONS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
  { days: 90, label: "90 jours" },
  { days: 180, label: "180 jours" },
  { days: 365, label: "1 an" },
];

const EXPIRY_OPTIONS = [
  { hours: 24, label: "24 heures" },
  { hours: 72, label: "3 jours" },
  { hours: 168, label: "7 jours" },
  { hours: 720, label: "30 jours" },
  { hours: 0, label: "Jamais" },
];

const MAX_USES_OPTIONS = [
  { value: 1, label: "1 (usage unique)" },
  { value: 5, label: "5 utilisations" },
  { value: 10, label: "10 utilisations" },
  { value: 25, label: "25 utilisations" },
  { value: 50, label: "50 utilisations" },
  { value: 100, label: "100 utilisations" },
];

interface CreatedInvitation {
  code: string;
  expiresAt: string | null;
  maxUses: number | null;
  metadata: Record<string, unknown>;
}

export function Invitations() {
  const [reason, setReason] = useState("");
  const [withGrant, setWithGrant] = useState(false);
  const [grantTier, setGrantTier] = useState<Tier>("pro");
  const [grantDays, setGrantDays] = useState(30);
  const [expiryHours, setExpiryHours] = useState(168);
  const [maxUses, setMaxUses] = useState<number | null>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<CreatedInvitation[]>([]);
  const [copied, setCopied] = useState<string | null>(null);
  const [quota, setQuota] = useState<{ used: number; limit: number; remaining: number } | null>(null);

  async function handleCreate() {
    if (!reason.trim() || reason.trim().length < 3) {
      setError("Raison requise (min. 3 caractères)");
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        reason: reason.trim(),
        maxUses,
      };
      if (expiryHours > 0) body.expiresInHours = expiryHours;
      if (withGrant) {
        body.grantTier = grantTier;
        body.grantDurationDays = grantDays;
      }
      const data = await api<{
        invitation: CreatedInvitation;
        quota: { used: number; limit: number; remaining: number };
      }>(
        "/staff/admin/invitations",
        { method: "POST", body: JSON.stringify(body) },
      );
      setCreated(prev => [data.invitation, ...prev]);
      setQuota(data.quota);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Erreur");
    } finally {
      setLoading(false);
    }
  }

  function copyCode(code: string) {
    void navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  }

  return (
    <div style={{ maxWidth: 640 }}>
      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <p style={{ margin: 0, fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(7,10,13,0.4)" }}>
          Back-office
        </p>
        <h1 style={{ margin: "4px 0 0", fontSize: 22, fontWeight: 700, color: "#070a0d", letterSpacing: "-0.02em" }}>
          Invitations
        </h1>
      </div>

      {/* Create form */}
      <div style={{
        background: "rgba(255,255,255,0.82)",
        border: "1px solid #fff",
        borderRadius: 16,
        backdropFilter: "blur(10px) saturate(145%)",
        boxShadow: "0 4px 24px rgba(0,0,0,0.07)",
        padding: 20,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}>
        <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#070a0d" }}>Générer une invitation</p>

        <div>
          <label className="bo-label">Raison</label>
          <input className="bo-input" type="text" placeholder="Raison de l'invitation…"
            value={reason} onChange={e => setReason(e.target.value)} />
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1 }}>
            <label className="bo-label">Expiration du lien</label>
            <select className="bo-input" value={expiryHours} onChange={e => setExpiryHours(Number(e.target.value))}>
              {EXPIRY_OPTIONS.map(o => <option key={o.hours} value={o.hours}>{o.label}</option>)}
            </select>
          </div>
          <div style={{ flex: 1 }}>
            <label className="bo-label">Utilisations max</label>
            <select className="bo-input"
              value={maxUses ?? 1}
              onChange={e => setMaxUses(Number(e.target.value))}>
              {MAX_USES_OPTIONS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Grant toggle */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, color: "#070a0d" }}>
          <input type="checkbox" checked={withGrant} onChange={e => setWithGrant(e.target.checked)} style={{ accentColor: "#b89c6a" }} />
          Inclure un abonnement cadeau à l'inscription
        </label>

        {withGrant && (
          <div style={{
            display: "flex", gap: 10,
            padding: 12, borderRadius: 12,
            background: "rgba(251,231,181,0.15)",
            border: "1px solid rgba(184,156,106,0.2)",
          }}>
            <div style={{ flex: 1 }}>
              <label className="bo-label">Tier</label>
              <select className="bo-input" value={grantTier} onChange={e => setGrantTier(e.target.value as Tier)}>
                {TIER_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label className="bo-label">Durée abonnement</label>
              <select className="bo-input" value={grantDays} onChange={e => setGrantDays(Number(e.target.value))}>
                {DURATION_OPTIONS.map(o => <option key={o.days} value={o.days}>{o.label}</option>)}
              </select>
            </div>
          </div>
        )}

        <button className="bo-btn" disabled={loading} onClick={() => void handleCreate()}
          style={{ alignSelf: "flex-start" }}>
          {loading ? "Création…" : "Générer l'invitation"}
        </button>

        {error && (
          <p style={{ margin: 0, fontSize: 11, color: "#c0392b" }}>{error}</p>
        )}

        {quota && (
          <div style={{
            display: "flex", alignItems: "center", gap: 8,
            padding: "8px 12px", borderRadius: 10,
            background: quota.remaining <= 2 ? "rgba(192,57,43,0.06)" : "rgba(7,10,13,0.03)",
            border: `1px solid ${quota.remaining <= 2 ? "rgba(192,57,43,0.12)" : "rgba(7,10,13,0.06)"}`,
          }}>
            <span style={{ fontSize: 11, color: "rgba(7,10,13,0.5)" }}>
              Quota journalier :
            </span>
            <span style={{
              fontSize: 11, fontWeight: 700,
              color: quota.remaining <= 2 ? "#c0392b" : "#2d7a5a",
            }}>
              {quota.used} / {quota.limit} utilisé{quota.used > 1 ? "s" : ""}
            </span>
            <span style={{ fontSize: 11, color: "rgba(7,10,13,0.4)" }}>
              — {quota.remaining} restante{quota.remaining > 1 ? "s" : ""}
            </span>
          </div>
        )}
      </div>

      {/* Created invitations */}
      {created.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <p style={{ margin: "0 0 12px", fontSize: 13, fontWeight: 700, color: "#070a0d" }}>
            Invitations générées ({created.length})
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {created.map((inv, i) => {
              const meta = inv.metadata as Record<string, unknown>;
              const hasGrant = !!meta.grantTier;
              return (
                <div key={i} style={{
                  background: "rgba(255,255,255,0.82)",
                  border: "1px solid #fff",
                  borderRadius: 12,
                  backdropFilter: "blur(10px) saturate(145%)",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.05)",
                  padding: "14px 16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 700, fontFamily: "monospace", color: "#070a0d", letterSpacing: "0.04em" }}>
                      {inv.code}
                    </p>
                    <div style={{ display: "flex", gap: 6, marginTop: 4, flexWrap: "wrap" }}>
                      {hasGrant && (
                        <span style={{
                          display: "inline-flex", height: 18, alignItems: "center",
                          borderRadius: 99, padding: "0 7px",
                          fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                          background: "rgba(251,231,181,0.25)", color: "#b89c6a",
                          border: "1px solid rgba(184,156,106,0.3)",
                        }}>
                          {String(meta.grantTier)} — {String(meta.grantDurationDays)}j
                        </span>
                      )}
                      <span style={{
                        display: "inline-flex", height: 18, alignItems: "center",
                        borderRadius: 99, padding: "0 7px",
                        fontSize: 9, fontWeight: 600,
                        background: "rgba(7,10,13,0.04)", color: "rgba(7,10,13,0.45)",
                        border: "1px solid rgba(7,10,13,0.08)",
                      }}>
                        {inv.maxUses} use{inv.maxUses !== 1 ? "s" : ""}
                      </span>
                      {inv.expiresAt && (
                        <span style={{
                          display: "inline-flex", height: 18, alignItems: "center",
                          borderRadius: 99, padding: "0 7px",
                          fontSize: 9, fontWeight: 600,
                          background: "rgba(7,10,13,0.04)", color: "rgba(7,10,13,0.45)",
                          border: "1px solid rgba(7,10,13,0.08)",
                        }}>
                          exp. {new Date(inv.expiresAt).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                    </div>
                  </div>
                  <button className="bo-btn"
                    onClick={() => copyCode(inv.code)}
                    style={{ fontSize: 11, padding: "4px 10px", whiteSpace: "nowrap" }}>
                    {copied === inv.code ? "✓ Copié" : "Copier"}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
